import React, { useEffect, useMemo, useState } from "react";
import { useVendorStore } from "../../store/vendorStore";

export default function VendorsPage() {
  const { items, loading, error, hasLoadedOnce, loadAll, addVendor } = useVendorStore();

  // search
  const [q, setQ] = useState("");

  // modal open
  const [show, setShow] = useState(false);

  // form state
  const [vendorName, setVendorName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [countryCode, setCountryCode] = useState("");

  useEffect(() => {
    if (!hasLoadedOnce) void loadAll();
  }, [hasLoadedOnce, loadAll]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((v) =>
      (v.vendorName || "").toLowerCase().includes(term) ||
      (v.contactEmail || "").toLowerCase().includes(term) ||
      (v.phoneNumber || "").toLowerCase().includes(term) ||
      (v.countryCode || "").toLowerCase().includes(term)
    );
  }, [q, items]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorName.trim()) {
      alert("Vendor name is required");
      return;
    }
    await addVendor({
      vendorName: vendorName.trim(),
      contactEmail: contactEmail.trim() || undefined,
      phoneNumber: phoneNumber.trim() || undefined,
      address: address.trim() || undefined,
      countryCode: countryCode.trim() || undefined,
    });
    // reset & close
    setVendorName("");
    setContactEmail("");
    setPhoneNumber("");
    setAddress("");
    setCountryCode("");
    setShow(false);
  };

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Vendors</h5>
        <div className="d-flex gap-2">
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Search vendors"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 260 }}
          />
          <button className="btn btn-sm btn-outline-secondary" onClick={() => loadAll()} disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Loading…
              </>
            ) : (
              "Refresh"
            )}
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setShow(true)}>
            + Add Vendor
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!hasLoadedOnce && loading ? (
        <div className="d-flex align-items-center gap-2 my-3">
          <div className="spinner-border" role="status" aria-label="Loading"></div>
          <span>Loading vendors…</span>
        </div>
      ) : null}

      <div className="table-responsive">
        <table className="table table-hover align-middle text-start">
          <thead className="table-light">
            <tr>
              <th className="text-start" style={{ width: 100 }}>ID</th>
              <th className="text-start">Name</th>
              <th className="text-start" style={{ width: 260 }}>Email</th>
              <th className="text-start" style={{ width: 160 }}>Phone</th>
              <th className="text-start" style={{ width: 120 }}>Country</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.vendorId}>
                <td className="text-start">#{v.vendorId}</td>
                <td className="text-start fw-semibold">{v.vendorName}</td>
                <td className="text-start">{v.contactEmail || "—"}</td>
                <td className="text-start">{v.phoneNumber || "—"}</td>
                <td className="text-start">{v.countryCode || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && hasLoadedOnce && !loading && (
              <tr>
                <td colSpan={5}>
                  <div className="alert alert-info mb-0">No vendors to display.</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Minimal bootstrap modal */}
      {show && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ background: "rgba(0,0,0,0.45)" }}
            onClick={() => setShow(false)}
          />

          <div
            className="modal d-block"
            tabIndex={-1}
            role="dialog"
            style={{ zIndex: 1055 }}
            onClick={() => setShow(false)}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <form className="modal-content" onSubmit={onSubmit}>
                <div className="modal-header">
                  <h6 className="modal-title">Add Vendor</h6>
                  <button type="button" className="btn-close" onClick={() => setShow(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Vendor Name *</label>
                    <input
                      className="form-control"
                      value={vendorName}
                      onChange={(e) => setVendorName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Contact Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Phone</label>
                    <input
                      className="form-control"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Country Code</label>
                    <input
                      className="form-control"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setShow(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Vendor
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
